import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Plus, Cake, Gift, ChevronRight, Share2, Bell, BellOff,
  Loader2, CalendarDays, Heart, Sparkles, UserPlus, X,
} from "lucide-react";
import ConciergeCard from "@/components/home/ConciergeCard";

function UpcomingBirthday({ friend, onShare, theme }) {
  const isUrgent = friend.days_until <= 7;
  const isToday = friend.days_until === 0;
  const bdayDate = friend.birthday
    ? new Date(friend.birthday + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.97 }}
      className={`min-w-[240px] max-w-[240px] rounded-2xl p-4 flex-shrink-0 cursor-pointer border transition-shadow hover:shadow-md ${
        isToday
          ? "bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200"
          : isUrgent
          ? "bg-gradient-to-br from-orange-50 to-red-50 border-orange-200"
          : "bg-white border-slate-100"
      }`}
      data-testid={`upcoming-birthday-${friend.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: isToday
              ? "#fbbf24"
              : isUrgent
              ? "#fb923c"
              : `${theme.primary}20`,
          }}
        >
          <Cake
            className="w-5 h-5"
            style={{
              color: isToday ? "#92400e" : isUrgent ? "#9a3412" : theme.primary,
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{friend.name}</p>
          <p className="text-[10px] text-slate-400">
            {bdayDate}
            {friend.relationship ? ` · ${friend.relationship}` : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
            isToday
              ? "bg-amber-200 text-amber-800"
              : isUrgent
              ? "bg-orange-100 text-orange-700"
              : "bg-slate-50 text-slate-500"
          }`}
        >
          {isToday ? "Today!" : friend.days_until === 1 ? "Tomorrow" : `${friend.days_until} days`}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShare(friend);
          }}
          className="p-1.5 rounded-lg hover:bg-slate-50 text-slate-300 hover:text-slate-500 transition"
          data-testid={`share-countdown-${friend.id}`}
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function QuickAddFriend({ onAdd, onCancel }) {
  const [name, setName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !birthday) return;
    setSaving(true);
    await onAdd({ name: name.trim(), birthday });
    setSaving(false);
    setName("");
    setBirthday("");
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3 overflow-hidden"
      data-testid="quick-add-friend-form"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-700">Add a birthday</p>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-green-300 focus:ring-1 focus:ring-green-200 outline-none"
        data-testid="quick-add-name"
        required
      />
      <input
        type="date"
        value={birthday}
        onChange={(e) => setBirthday(e.target.value)}
        className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-green-300 focus:ring-1 focus:ring-green-200 outline-none"
        data-testid="quick-add-birthday"
        required
      />
      <button
        type="submit"
        disabled={saving || !name.trim() || !birthday}
        className="w-full py-2.5 rounded-xl bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition disabled:opacity-50"
        data-testid="quick-add-submit"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
        ) : (
          "Add"
        )}
      </button>
    </motion.form>
  );
}

function EmptyFriendsState({ onAdd, theme }) {
  return (
    <div className="text-center py-10 px-6" data-testid="friends-empty-state">
      <div
        className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: `${theme.primary}15` }}
      >
        <Users className="w-8 h-8" style={{ color: theme.primary }} />
      </div>
      <h3 className="text-lg font-bold text-slate-800 mb-1">
        Track birthdays that matter
      </h3>
      <p className="text-sm text-slate-400 mb-6 max-w-[260px] mx-auto">
        Never miss a friend's birthday. Add people you care about and we'll help you plan the perfect birthday deal run for them.
      </p>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-bold shadow-lg transition hover:opacity-90"
        style={{ backgroundColor: theme.primary }}
        data-testid="friends-empty-add-btn"
      >
        <Plus className="w-4 h-4" />
        Add your first birthday
      </button>
    </div>
  );
}

export default function FriendsHome() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/friends/birthdays`);
      setFriends(res.data.friends || []);
    } catch {}
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFriends();
  }, [fetchFriends]);

  const handleAdd = async (data) => {
    try {
      await axios.post(`${API}/friends/birthdays`, data);
      toast.success(`Added ${data.name}'s birthday`);
      setShowAdd(false);
      fetchFriends();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const handleShare = async (friend) => {
    const url = `${window.location.origin}/friend-countdown/${friend.share_token}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${friend.name}'s Birthday Countdown`,
          text: `${friend.days_until} days until ${friend.name}'s birthday!`,
          url,
        });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Countdown link copied!");
    }
  };

  const upcoming = friends.filter((f) => f.days_until <= 30);
  const later = friends.filter((f) => f.days_until > 30);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2
          className="w-6 h-6 animate-spin"
          style={{ color: theme.primary }}
        />
      </div>
    );
  }

  return (
    <motion.div
      key="friends-mode"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="friends-mode"
    >
      {/* Friends header */}
      <div className="px-4 mt-4 mb-4">
        <h2
          className="text-lg font-bold text-slate-800"
          data-testid="friends-feed-header"
        >
          Friends & Birthdays
        </h2>
        <p className="text-[12px] text-slate-400 mt-0.5">
          {friends.length > 0
            ? `Tracking ${friends.length} birthday${friends.length !== 1 ? "s" : ""}${upcoming.length > 0 ? ` · ${upcoming.length} coming up` : ""}`
            : "Never miss a birthday again"}
        </p>
      </div>

      {friends.length === 0 ? (
        <div className="px-4">
          <EmptyFriendsState onAdd={() => setShowAdd(true)} theme={theme} />
        </div>
      ) : (
        <>
          {/* Upcoming birthdays carousel */}
          {upcoming.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between px-4 mb-2.5">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <Sparkles
                    className="w-4 h-4"
                    style={{ color: theme.primary }}
                  />
                  Coming up
                </h3>
                <span className="text-[10px] font-semibold text-slate-400">
                  within 30 days
                </span>
              </div>
              <div className="overflow-x-auto pb-2 -mx-4 px-4">
                <div className="flex gap-3">
                  {upcoming.map((f) => (
                    <UpcomingBirthday
                      key={f.id}
                      friend={f}
                      onShare={handleShare}
                      theme={theme}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* All friends list */}
          <div className="px-4 mb-4">
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-sm font-bold text-slate-700">
                All birthdays
              </h3>
              <button
                onClick={() => setShowAdd(true)}
                className="p-2 rounded-xl text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: theme.primary }}
                data-testid="friends-add-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <AnimatePresence>
              {showAdd && (
                <div className="mb-3">
                  <QuickAddFriend
                    onAdd={handleAdd}
                    onCancel={() => setShowAdd(false)}
                  />
                </div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              {friends.map((f, i) => {
                const isToday = f.days_until === 0;
                const isUrgent = f.days_until <= 7;
                const bdayDate = f.birthday
                  ? new Date(f.birthday + "T00:00:00").toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" }
                    )
                  : "";
                return (
                  <motion.div
                    key={f.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 cursor-pointer hover:shadow-sm transition"
                    data-testid={`friend-list-${f.id}`}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: isToday
                          ? "#fef3c7"
                          : isUrgent
                          ? "#fff7ed"
                          : `${theme.primary}10`,
                      }}
                    >
                      <Cake
                        className="w-4 h-4"
                        style={{
                          color: isToday
                            ? "#b45309"
                            : isUrgent
                            ? "#ea580c"
                            : theme.primary,
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-700 truncate">
                        {f.name}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {bdayDate}
                        {f.relationship ? ` · ${f.relationship}` : ""}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-bold flex-shrink-0 ${
                        isToday
                          ? "text-amber-600"
                          : isUrgent
                          ? "text-orange-500"
                          : "text-slate-400"
                      }`}
                    >
                      {isToday
                        ? "Today!"
                        : f.days_until === 1
                        ? "Tomorrow"
                        : `${f.days_until}d`}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-200 flex-shrink-0" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Quick action concierge cards */}
      <div className="px-4 mb-5 space-y-2" data-testid="friends-concierge-cards">
        <ConciergeCard
          icon={Gift}
          title="Browse Deals for a Friend"
          subtitle="Find the best birthday deals in their area"
          to="/my-deals"
          accentColor={theme.primary}
          delay={0}
          testId="concierge-friend-deals"
        />
        <ConciergeCard
          icon={Heart}
          title="Plan a Birthday Surprise"
          subtitle="Curate an itinerary and share it"
          to="/plan-birthday"
          accentColor="#ec4899"
          delay={0.05}
          testId="concierge-surprise"
        />
        <ConciergeCard
          icon={UserPlus}
          title="Invite Friends to the App"
          subtitle="Share your referral code and earn points"
          to="/profile"
          accentColor="#8b5cf6"
          delay={0.1}
          testId="concierge-invite"
        />
      </div>

    </motion.div>
  );
}
